import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useLayoutEffect, useRef, } from 'react';
import { cn } from '../lib/utils.js';
const controlBase = [
    'flex rounded-lg border-[0.5px] border-line-strong bg-surface px-3 py-2',
    'transition-[border-color,box-shadow] duration-[120ms] ease-out min-w-0',
    'has-[textarea:focus]:border-accent has-[textarea:focus]:shadow-[0_0_0_3px_var(--color-accent-soft)]',
];
/**
 * Textarea — multiline field mirroring Input's API (label / hint / error /
 * required / mono) and control wrapper + focus ring. `forwardRef` reaches the
 * textarea; arbitrary props spread onto it (react-hook-form `register`, `data-*`).
 * `autoGrow` grows the field to its content. The note (error ?? hint) sits
 * outside the label so it never pollutes the field's accessible name; it is
 * wired back with `aria-describedby`, and `error` sets `aria-invalid`.
 */
export const Textarea = forwardRef(({ className, label, required = false, hint, error, mono = false, autoGrow = false, rows = 3, value, defaultValue, onInput, 'aria-describedby': ariaDescribedBy, ...rest }, ref) => {
    const innerRef = useRef(null);
    const noteId = useId();
    const note = error ?? hint;
    const describedBy = [note ? noteId : null, ariaDescribedBy].filter(Boolean).join(' ') || undefined;
    const setRefs = useCallback((node) => {
        innerRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    const resize = useCallback(() => {
        const node = innerRef.current;
        if (!node || !autoGrow)
            return;
        node.style.height = 'auto';
        node.style.height = `${node.scrollHeight}px`;
    }, [autoGrow]);
    useLayoutEffect(resize, [resize, value, defaultValue]);
    return (_jsxs("div", { className: cn('flex min-w-0 flex-col gap-[5px]', className), children: [_jsxs("label", { className: "flex min-w-0 flex-col gap-[5px]", children: [label && (_jsxs("span", { className: "font-ui text-[11px] font-medium uppercase tracking-[0.06em] text-ink-4", children: [label, required && (_jsx("span", { "aria-hidden": "true", className: "text-danger", children: ' *' }))] })), _jsx("span", { className: cn(controlBase, error && [
                            'border-danger has-[textarea:focus]:shadow-[0_0_0_3px_var(--color-danger-soft)]',
                        ]), children: _jsx("textarea", { ref: setRefs, rows: rows, required: required, value: value, defaultValue: defaultValue, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy, onInput: (event) => {
                                resize();
                                onInput?.(event);
                            }, className: cn('w-full min-w-0 flex-1 resize-y border-0 bg-transparent p-0 font-ui text-[13.5px] leading-[1.5] text-ink outline-none placeholder:text-ink-4', mono && 'font-mono text-[13px] tabular-nums', autoGrow && 'resize-none overflow-hidden'), ...rest }) })] }), note && (_jsx("span", { id: noteId, className: cn('font-ui text-xs text-ink-4', error && 'text-danger'), children: note }))] }));
});
Textarea.displayName = 'Textarea';
//# sourceMappingURL=textarea.js.map